"use client";

import { useEffect, useRef, useState } from "react";
import { PrismicRichText, PrismicText, PrismicImage, PrismicLink } from "@prismicio/react";
import { isFilled, asLink } from "@prismicio/client";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import '@splidejs/react-splide/css/core';
import "./carrousel-slider.css";

const colorMap = {
  'light-pink': '#F1AEC0',
  'light-green': '#DFE05B',
  'light-orange': '#FCC453',
};

const STICKER_CONFIG = {
  "pink": {
    src: "/img/sticker-carrousel-pink.svg",
    textClass: "text-dark-pink",
  },
  "green": {
    src: "/img/sticker-carrousel-green.svg",
    textClass: "text-dark-green",
  },
  "orange": {
    src: "/img/sticker-carrousel-orange.svg",
    textClass: "text-moyam-orange",
  },
  "default": {
    src: "/img/sticker-carrousel-pink.svg",
    textClass: "text-dark-pink",
  }
};

const getStickerConfig = (colorName) => {
  return STICKER_CONFIG[colorName] || STICKER_CONFIG["default"];
};

const CardDefault = ({ category }) => {
  const [isHovered, setIsHovered] = useState(false);
  const hasLink = isFilled.link(category.cta);
  const hasAnchor = isFilled.keyText(category.anchor);
  const linkProps = {};
  if (hasLink && hasAnchor) {
    const ctaUrl = asLink(category.cta);
    linkProps.href = `${ctaUrl}#${category.anchor}`;
  } else if (hasLink) {
    linkProps.field = category.cta;
  } else if (hasAnchor) {
    linkProps.href = `#${category.anchor}`;
  }
  let Wrapper = 'div';
  if (hasLink && hasAnchor) {
    Wrapper = 'a';
  } else if (hasLink) {
    Wrapper = PrismicLink;
  } else if (hasAnchor) {
    Wrapper = 'a';
  }

  return (
    <Wrapper
      {...linkProps}
      className={`relative z-10 w-full h-95 md:h-143 transition duration-300 flex flex-col justify-end group ${hasLink || hasAnchor ? 'no-underline' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isFilled.image(category.img) && (
        <div className="hidden md:block absolute h-full w-full z-0 overflow-hidden">
          <PrismicImage
            className={`h-full w-full object-cover  group-hover:scale-115 transition-transform duration-500`}
            field={{ ...category.img, alt: category.img.alt || "Image d'illustration" }}
          />
          <div className="absolute inset-0 z-1 bg-gradient-to-b from-transparent via-transparent to-black/60"></div>
        </div>
      )}

      {isFilled.image(category.img_mobile) && (
        <>
          <PrismicImage
            className="md:hidden absolute h-full w-full z-0 object-cover"
            field={{ ...category.img_mobile, alt: category.img_mobile.alt || "Image d'illustration" }}
          />
          <div className="md:hidden absolute inset-0 z-[1] bg-gradient-to-b from-transparent via-transparent to-black/40" style={{ backgroundImage: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 34.63%, rgba(0, 0, 0, 0.40) 79.31%)' }}></div>
        </>
      )}
      <div className={`relative z-10 px-8 pb-6 flex items-center text-beige group-hover:text-${category.hover_color}`}>
        <div className="md:pr-5 2xl:pr-16">
          {isFilled.keyText(category.title) && (
            <h3 className="text-base md:text-xl font-bold font-body">{category.title}</h3>
          )}

          {isFilled.keyText(category.text) && (
            <p className="text-base md:text-xl font-body">{category.text}</p>
          )}
        </div>
        {(hasLink || hasAnchor) && (
          <div className="hidden md:block">
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="transition-colors duration-300 h-7 w-7 xl:h-10 xl:w-10"
            >
              <path d="M20 0C16.0444 0 12.1776 1.17298 8.8886 3.37061C5.59962 5.56823 3.03617 8.69181 1.52242 12.3463C0.00866569 16.0008 -0.387401 20.0222 0.384303 23.9018C1.15601 27.7814 3.06082 31.3451 5.85787 34.1421C8.65492 36.9392 12.2186 38.844 16.0982 39.6157C19.9778 40.3874 23.9991 39.9913 27.6537 38.4776C31.3082 36.9638 34.4318 34.4004 36.6294 31.1114C38.827 27.8224 40 23.9556 40 20C39.9944 14.6974 37.8855 9.61356 34.136 5.86405C30.3864 2.11453 25.3026 0.00559965 20 0ZM28.7808 21.0885L22.6269 27.2423C22.3382 27.531 21.9467 27.6932 21.5385 27.6932C21.1302 27.6932 20.7387 27.531 20.45 27.2423C20.1613 26.9536 19.9991 26.5621 19.9991 26.1538C19.9991 25.7456 20.1613 25.3541 20.45 25.0654L23.9788 21.5385H12.3077C11.8997 21.5385 11.5084 21.3764 11.2198 21.0879C10.9313 20.7993 10.7692 20.408 10.7692 20C10.7692 19.592 10.9313 19.2007 11.2198 18.9121C11.5084 18.6236 11.8997 18.4615 12.3077 18.4615H23.9788L20.45 14.9346C20.1613 14.6459 19.9991 14.2544 19.9991 13.8462C19.9991 13.4379 20.1613 13.0464 20.45 12.7577C20.7387 12.469 21.1302 12.3068 21.5385 12.3068C21.9467 12.3068 22.3382 12.469 22.6269 12.7577L28.7808 18.9115C28.9238 19.0544 29.0373 19.2241 29.1147 19.4109C29.1921 19.5976 29.232 19.7978 29.232 20C29.232 20.2022 29.1921 20.4024 29.1147 20.5891C29.0373 20.7759 28.9238 20.9456 28.7808 21.0885Z"
                fill={isHovered ? colorMap[category.hover_color] || '#FDF6D2' : '#FDF6D2'} />
            </svg>
          </div>
        )}
      </div>
    </Wrapper>
  );
};

/**
 * @typedef {import("@prismicio/client").Content.CarrouselSlice} CarrouselSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<CarrouselSlice>} CarrouselProps
 * @type {import("react").FC<CarrouselProps>}
 */
const CarouselDefault = ({ slice }) => {
  const categories = slice.primary.categories || [];
  const [deviceType, setDeviceType] = useState("desktop");

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 768) {
        setDeviceType("mobile");
      } else if (width >= 768 && width < 1110) {
        setDeviceType("tablet");
      } else if (width >= 1110 && width < 1600) {
        setDeviceType("desktop");
      } else {
        setDeviceType("xlarge");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const showMobileSlider = deviceType === "mobile" && categories.length >= 1;
  const showTabletSlider = deviceType === "tablet" && categories.length > 1;
  const showDesktopSlider = deviceType === "desktop" && categories.length > 2;
  const showXlargeSlider = deviceType === "xlarge" && categories.length > 4;
  const showDesktopGrid = deviceType === "desktop" && categories.length <= 2;
  const showTabletGrid = deviceType === "tablet" && categories.length <= 1;
  const showXlargeGrid = deviceType === "xlarge" && categories.length <= 4;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`${slice.variation === "default" ? "bg-beige pt-10 md:py-20" : ""} relative flex flex-col overflow-visible gap-6`}
    >
      {slice.variation === "default" && (
        <>
          {isFilled.group(slice.primary.sticker) && slice.primary.sticker[0] && (
            <div className="absolute w-30 h-24 md:w-60 md:h-38 top-0 left-4 md:left-8 z-40">
              <div className="relative inline-block flex items-center w-full h-full">
                {isFilled.keyText(slice.primary.sticker[0].text) && (
                  <h2 className={`p-5 text-center -rotate-11 relative z-40 text-sticker ${getStickerConfig(slice.primary.sticker[0].bg_color).textClass}`}>
                    {slice.primary.sticker[0].text}
                  </h2>
                )}
                <img
                  src={getStickerConfig(slice.primary.sticker[0].bg_color).src}
                  alt=""
                  className="absolute w-full h-full pointer-events-none object-fill -top-[2px] md:-top-[15px]"
                />
              </div>
            </div>
          )}

          {isFilled.group(slice.primary.categories) && (
            <>
              {showMobileSlider && (
                <div>
                  <div className="flex snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] overflow-x-scroll">
                    {categories.map((category, index) => (
                      <div
                        key={index}
                        className="snap-center snap-always flex-shrink-0 w-[75vw] max-w-[360px]"
                      >
                        <CardDefault
                          category={category}
                          isCarousel={false}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {showDesktopSlider && (
                <div className="overflow-visible carrousel-default">
                  <Splide
                    options={{
                      perPage: 3,
                      perMove: 1,
                      gap: "0rem",
                      pagination: false,
                      arrows: true,
                      width: "100%",
                      padding: { right: "2.5rem", left: "5rem" },
                    }}
                    aria-label="Gammes"
                  >
                    {categories.map((category, index) => (
                      <SplideSlide key={index} className="group overflow-visible">
                        <CardDefault
                          category={category}
                          isCarousel={true}
                        />
                      </SplideSlide>
                    ))}
                  </Splide>
                </div>
              )}

              {showXlargeSlider && (
                <div className="overflow-visible carrousel-default">
                  <Splide
                    options={{
                      perPage: 4,
                      perMove: 1,
                      gap: "0rem",
                      pagination: false,
                      arrows: true,
                      width: "100%",
                      padding: { right: "2.5rem", left: "5rem" },
                    }}
                    aria-label="Gammes"
                  >
                    {categories.map((category, index) => (
                      <SplideSlide key={index} className="group overflow-visible">
                        <CardDefault
                          category={category}
                          isCarousel={true}
                        />
                      </SplideSlide>
                    ))}
                  </Splide>
                </div>
              )}

              {showTabletSlider && (
                <div className="overflow-visible carrousel-default">
                  <Splide
                    options={{
                      perPage: 2,
                      perMove: 1,
                      gap: "0rem",
                      pagination: false,
                      arrows: true,
                      width: "100%",
                      padding: { right: "2.5rem", left: "5rem" },
                    }}
                    aria-label="Gammes"
                  >
                    {categories.map((category, index) => (
                      <SplideSlide key={index} className="group overflow-visible">
                        <CardDefault
                          category={category}
                          isCarousel={true}
                        />
                      </SplideSlide>
                    ))}
                  </Splide>
                </div>
              )}

              {showDesktopGrid && (
                <div className="px-20 flex flex-row justify-center">
                  {categories.map((category, index) => (
                    <div key={index} className="group w-1/3">
                      <CardDefault
                        category={category}
                        isCarousel={false}
                      />
                    </div>
                  ))}
                </div>
              )}

              {showXlargeGrid && (
                <div className="px-20 flex flex-row justify-center">
                  {categories.map((category, index) => (
                    <div key={index} className="group w-1/4">
                      <CardDefault
                        category={category}
                        isCarousel={false}
                      />
                    </div>
                  ))}
                </div>
              )}

              {showTabletGrid && (
                <div className="px-20 flex flex-row justify-center">
                  {categories.map((category, index) => (
                    <div key={index} className="group w-1/2">
                      <CardDefault
                        category={category}
                        isCarousel={false}
                      />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
};

export default CarouselDefault;
